// Moment Search query engine — shared by chat keyword search (twitch-chat.service.ts)
// and transcript text search (transcript.service.ts). Pure functions, no Angular.
// Moved verbatim from twitch-chat.service.ts (2026-10-06) — behavior must stay identical:
// see src/CLAUDE.md «Moment Search» for the query syntax contract.

export interface ParsedSearchQuery {
  // AND of OR-groups: every group must match at least one term.
  // Single-term group = plain required word. Multi-term group = `word1|word2|word3` or `word1, word2`.
  positive: string[][];
  phrases: string[];       // exact multi-word substrings (no stemming)
  negative: string[];      // excluded words (stem-matched — respects exactMatch toggle)
  negativeExact: string[]; // excluded words (always literal, bypasses stemmer — syntax: -"word")
}

// --- Russian stemmer for search morphology tolerance ---
// Closed set: removes common inflectional endings to compare word roots.
// Sorted longest-first so "ями" wins over "и" on "словами".
// Min stem length 3 guards against overstripping on short roots ("мат" stays "мат", not "м").
const RU_ENDINGS: string[] = [
  // plural instrumentals / dative / loc
  'иями', 'ями', 'ами', 'иях', 'ях',
  // adjective endings
  'ого', 'его', 'ому', 'ему', 'ыми', 'ими', 'ыми',
  'ое', 'ее', 'ая', 'яя', 'ую', 'юю', 'ые', 'ие', 'ый', 'ий', 'ой', 'ом', 'ем', 'ым', 'им',
  // verb present/future
  'ешь', 'ишь', 'ете', 'ите', 'ют', 'ут', 'ат', 'ят', 'ем', 'им',
  // verb past
  'ла', 'ло', 'ли', 'л',
  // verb infinitive
  'ться', 'ться', 'ть', 'чь',
  // noun plural genitive / singular
  'ов', 'ев', 'ей',
  // singular case endings + short
  'а', 'у', 'е', 'и', 'о', 'ы', 'я', 'ю', 'й', 'ь',
];
const RU_ENDINGS_SORTED: string[] = Array.from(new Set(RU_ENDINGS))
  .sort((a, b) => b.length - a.length);
const RU_MIN_STEM = 3;

export function russianStem(word: string): string {
  if (word.length <= RU_MIN_STEM) return word;
  for (const end of RU_ENDINGS_SORTED) {
    if (word.length - end.length >= RU_MIN_STEM && word.endsWith(end)) {
      return word.slice(0, -end.length);
    }
  }
  return word;
}

export function parseSearchQuery(raw: string): ParsedSearchQuery {
  // Normalize OR separators: commas and pipes (with optional surrounding whitespace) become '|'.
  // Outside quotes only — inside quotes commas stay literal.
  // Example: "мина, граната бот" -> "мина|граната бот"  → groups: [мина|граната], [бот]
  let normalized = '';
  let inQuoteN = false;
  for (const ch of raw) {
    if (ch === '"') { inQuoteN = !inQuoteN; normalized += ch; continue; }
    if (!inQuoteN && (ch === ',' || ch === '|')) {
      // Collapse any trailing spaces into the single pipe
      normalized = normalized.replace(/\s+$/, '') + '|';
    } else if (!inQuoteN && /\s/.test(ch) && normalized.endsWith('|')) {
      // Swallow leading spaces after a pipe
    } else {
      normalized += ch;
    }
  }

  const tokens: string[] = [];
  let current = '';
  let inQuote = false;
  let quoteIsNegation = false; // true when the current/next "..." is prefixed with `-` (exact negation)
  for (const ch of normalized) {
    if (ch === '"') {
      if (inQuote) {
        if (current) {
          tokens.push((quoteIsNegation ? 'NEGPHRASE:' : 'PHRASE:') + current);
        }
        current = '';
        inQuote = false;
        quoteIsNegation = false;
      } else {
        // Opening quote. A bare leading `-` is absorbed as the exact-negation prefix.
        if (current === '-') {
          quoteIsNegation = true;
          current = '';
        } else if (current) {
          tokens.push(current);
          current = '';
        }
        inQuote = true;
      }
    } else if (/\s/.test(ch) && !inQuote) {
      if (current) { tokens.push(current); current = ''; }
    } else {
      current += ch;
    }
  }
  if (current) {
    if (inQuote) tokens.push((quoteIsNegation ? 'NEGPHRASE:' : 'PHRASE:') + current);
    else tokens.push(current);
  }

  const positive: string[][] = [];
  const phrases: string[] = [];
  const negative: string[] = [];
  const negativeExact: string[] = [];
  for (const t of tokens) {
    if (t.startsWith('NEGPHRASE:')) {
      const w = t.slice(10).trim();
      if (w) negativeExact.push(w);
    } else if (t.startsWith('PHRASE:')) {
      phrases.push(t.slice(7));
    } else if (t.startsWith('-') && t.length > 1) {
      // Negation ignores OR (mixing -a|-b is semantically ambiguous); take first segment as the excluded word.
      const core = t.slice(1).split('|')[0];
      if (core) negative.push(core);
    } else if (t) {
      const group = t.split('|').filter(s => s.length > 0);
      if (group.length > 0) positive.push(group);
    }
  }
  return { positive, phrases, negative, negativeExact };
}

/** Does `body` satisfy the parsed query? (formerly TwitchChatService.matchComment) */
export function matchText(
  body: string,
  parsed: ParsedSearchQuery,
  exactMatch: boolean
): { matched: boolean; matchedKeywords: string[] } {
  const text = body.toLocaleLowerCase('ru');
  // Split on any non-letter/non-digit character (Unicode aware)
  const words = text.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  const exactWordSet = new Set(words);                         // literal word-match (no stem)
  const wordSet = exactMatch ? exactWordSet : new Set(words.map(russianStem));

  const normalize = (s: string) => {
    const lower = s.toLocaleLowerCase('ru');
    return exactMatch ? lower : russianStem(lower);
  };

  for (const neg of parsed.negative) {
    if (wordSet.has(normalize(neg))) return { matched: false, matchedKeywords: [] };
  }
  // Exact-word negations bypass the stemmer on this specific term, regardless of the global toggle.
  for (const neg of parsed.negativeExact) {
    if (exactWordSet.has(neg.toLocaleLowerCase('ru'))) return { matched: false, matchedKeywords: [] };
  }

  const matched: string[] = [];
  // Each positive group is an OR-set — at least one of its terms must be present.
  for (const group of parsed.positive) {
    let anyHit = false;
    for (const term of group) {
      if (wordSet.has(normalize(term))) {
        anyHit = true;
        matched.push(term);
      }
    }
    if (!anyHit) return { matched: false, matchedKeywords: [] };
  }
  for (const phrase of parsed.phrases) {
    const p = phrase.toLocaleLowerCase('ru');
    if (!text.includes(p)) return { matched: false, matchedKeywords: [] };
    matched.push(`"${phrase}"`);
  }
  return { matched: true, matchedKeywords: matched };
}

/** Display keywords for highlighting: OR-groups flattened, phrases re-quoted. */
export function displayKeywords(parsed: ParsedSearchQuery): string[] {
  return [...parsed.positive.flat(), ...parsed.phrases.map(p => `"${p}"`)];
}

/**
 * Split a text into char-level segments marking which characters are search matches.
 * Applies the same stemmer used during search, so «мину» gets highlighted when the query was «мина».
 * Phrases (wrapped in quotes) are matched by substring; everything else is stem-matched word-by-word.
 */
export function highlightMatchSegments(
  text: string,
  matchedKeywords: string[] | undefined,
  exactMatch: boolean
): Array<{ text: string; isMatch: boolean }> {
  if (!matchedKeywords?.length || !text) return [{ text, isMatch: false }];

  const phrases: string[] = [];
  const wordTargets = new Set<string>();
  for (const k of matchedKeywords) {
    if (k.length >= 2 && k.startsWith('"') && k.endsWith('"')) {
      const p = k.slice(1, -1).toLocaleLowerCase('ru');
      if (p) phrases.push(p);
    } else {
      const w = k.toLocaleLowerCase('ru');
      wordTargets.add(exactMatch ? w : russianStem(w));
    }
  }

  const ranges: Array<[number, number]> = [];
  // Stem-matched word hits
  const wordRegex = /\p{L}[\p{L}\p{N}]*/gu;
  let m: RegExpExecArray | null;
  while ((m = wordRegex.exec(text)) !== null) {
    const lower = m[0].toLocaleLowerCase('ru');
    const stem = exactMatch ? lower : russianStem(lower);
    if (wordTargets.has(stem)) ranges.push([m.index, m.index + m[0].length]);
  }
  // Phrase substring hits
  if (phrases.length > 0) {
    const lowerText = text.toLocaleLowerCase('ru');
    for (const phrase of phrases) {
      let idx = 0;
      while ((idx = lowerText.indexOf(phrase, idx)) !== -1) {
        ranges.push([idx, idx + phrase.length]);
        idx += phrase.length;
      }
    }
  }

  if (ranges.length === 0) return [{ text, isMatch: false }];

  // Merge overlapping ranges
  ranges.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [ranges[0]];
  for (let i = 1; i < ranges.length; i++) {
    const last = merged[merged.length - 1];
    if (ranges[i][0] <= last[1]) last[1] = Math.max(last[1], ranges[i][1]);
    else merged.push(ranges[i]);
  }

  const out: Array<{ text: string; isMatch: boolean }> = [];
  let pos = 0;
  for (const [s, e] of merged) {
    if (s > pos) out.push({ text: text.slice(pos, s), isMatch: false });
    out.push({ text: text.slice(s, e), isMatch: true });
    pos = e;
  }
  if (pos < text.length) out.push({ text: text.slice(pos), isMatch: false });
  return out;
}
